import { ROTULO_DA_FAIXA, type Faixa } from "../../nucleo/sucesso";

export interface PontoDaEvolucao {
  dia: string;
  score: number;
  faixa: Faixa;
}

const LARGURA = 640;
const ALTURA = 220;
const MARGEM = { topo: 12, direita: 16, baixo: 28, esquerda: 36 };

const COR_DA_FAIXA: Record<Faixa, string> = {
  saudavel: "var(--good)",
  atencao: "var(--warning)",
  risco: "var(--critical)",
};

/** `2026-10-05` -> `05/10`. */
function diaCurto(dia: string): string {
  const [, mes, numero] = dia.split("-");
  return `${numero}/${mes}`;
}

/** Posição horizontal de cada ponto: espaçada pela data, não pela ordem. */
function posicoesNoEixo(dias: readonly string[]): number[] {
  if (dias.length === 0) return [];
  const tempos = dias.map((dia) => Date.parse(`${dia}T00:00:00Z`));
  const primeiro = tempos[0] ?? 0;
  const ultimo = tempos[tempos.length - 1] ?? 0;
  const largura = LARGURA - MARGEM.esquerda - MARGEM.direita;
  if (ultimo === primeiro) return tempos.map(() => MARGEM.esquerda + largura / 2);
  return tempos.map((t) => MARGEM.esquerda + ((t - primeiro) / (ultimo - primeiro)) * largura);
}

function posicaoVertical(score: number): number {
  const altura = ALTURA - MARGEM.topo - MARGEM.baixo;
  return MARGEM.topo + (1 - score / 100) * altura;
}

/**
 * A linha do score ao longo dos dias. Os cortes das faixas aparecem como linhas tracejadas, e
 * cada ponto leva a cor da faixa em que estava. Uma tabela com os mesmos números acompanha o
 * gráfico (`PontosEmTabela`): a cor sozinha não é o portador da informação.
 */
export function EvolucaoDaSaude({
  pontos,
  limiteSaudavel,
  limiteAtencao,
}: {
  pontos: readonly PontoDaEvolucao[];
  limiteSaudavel: number;
  limiteAtencao: number;
}) {
  if (pontos.length === 0) {
    return <p data-testid="evolucao-vazia">Ainda não há retrato diário desta organização.</p>;
  }
  const x = posicoesNoEixo(pontos.map((p) => p.dia));
  const caminho = pontos.map((p, i) => `${x[i]},${posicaoVertical(p.score)}`).join(" ");
  const ultimo = pontos[pontos.length - 1] as PontoDaEvolucao;
  return (
    <figure data-testid="evolucao-da-saude">
      <svg
        viewBox={`0 0 ${LARGURA} ${ALTURA}`}
        role="img"
        aria-label={`Evolução do score de saúde: de ${pontos[0]?.score} em ${diaCurto(pontos[0]?.dia ?? "")} para ${ultimo.score} em ${diaCurto(ultimo.dia)}`}
        style={{ width: "100%", height: "auto" }}
      >
        {[0, 50, 100].map((marca) => (
          <g key={marca}>
            <line
              x1={MARGEM.esquerda}
              x2={LARGURA - MARGEM.direita}
              y1={posicaoVertical(marca)}
              y2={posicaoVertical(marca)}
              style={{ stroke: "var(--line)" }}
            />
            <text
              x={MARGEM.esquerda - 6}
              y={posicaoVertical(marca) + 4}
              textAnchor="end"
              fontSize="12"
              style={{ fill: "var(--ink-3)" }}
            >
              {marca}
            </text>
          </g>
        ))}
        {[
          { valor: limiteSaudavel, rotulo: "saudável" },
          { valor: limiteAtencao, rotulo: "atenção" },
        ].map((corte) => (
          <g key={corte.rotulo} data-testid={`corte-${corte.rotulo}`}>
            <line
              x1={MARGEM.esquerda}
              x2={LARGURA - MARGEM.direita}
              y1={posicaoVertical(corte.valor)}
              y2={posicaoVertical(corte.valor)}
              strokeDasharray="4 4"
              style={{ stroke: "var(--rule)" }}
            />
            <text
              x={LARGURA - MARGEM.direita}
              y={posicaoVertical(corte.valor) - 3}
              textAnchor="end"
              fontSize="12"
              style={{ fill: "var(--ink-3)" }}
            >
              {corte.rotulo} a partir de {corte.valor}
            </text>
          </g>
        ))}
        <polyline points={caminho} fill="none" strokeWidth="2" style={{ stroke: "var(--brand-deep)" }} />
        {pontos.map((ponto, indice) => (
          <circle
            key={ponto.dia}
            cx={x[indice]}
            cy={posicaoVertical(ponto.score)}
            r="4"
            data-faixa={ponto.faixa}
            style={{ fill: COR_DA_FAIXA[ponto.faixa], stroke: "var(--surface-1)" }}
          >
            <title>{`${diaCurto(ponto.dia)}: ${ponto.score} (${ROTULO_DA_FAIXA[ponto.faixa]})`}</title>
          </circle>
        ))}
        <text x={MARGEM.esquerda} y={ALTURA - 8} fontSize="12" style={{ fill: "var(--ink-3)" }}>
          {diaCurto(pontos[0]?.dia ?? "")}
        </text>
        <text
          x={LARGURA - MARGEM.direita}
          y={ALTURA - 8}
          textAnchor="end"
          fontSize="12"
          style={{ fill: "var(--ink-3)" }}
        >
          {diaCurto(ultimo.dia)}
        </text>
      </svg>
      <PontosEmTabela pontos={pontos} />
    </figure>
  );
}

function PontosEmTabela({ pontos }: { pontos: readonly PontoDaEvolucao[] }) {
  const recentes = [...pontos].reverse().slice(0, 10);
  return (
    <details className="mt-2">
      <summary>Ver os números dos últimos dias</summary>
      <table className="tabela" data-testid="evolucao-em-tabela">
        <caption className="sr-only">Score de saúde por dia</caption>
        <thead>
          <tr>
            <th scope="col">Dia</th>
            <th scope="col">Score</th>
            <th scope="col">Faixa</th>
          </tr>
        </thead>
        <tbody>
          {recentes.map((ponto) => (
            <tr key={ponto.dia}>
              <td>{diaCurto(ponto.dia)}</td>
              <td>{ponto.score}</td>
              <td>{ROTULO_DA_FAIXA[ponto.faixa]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}
